import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "../lib/cn";

/**
 * ActionInput — the generalized "input with one trailing CTA" primitive.
 *
 * This is the loose-coupling base. ChatInput, ExploreSearchBar, landing-style
 * email-capture, single-key URL entry — all are thin specializations of this.
 *
 * Behavior:
 * - Renders an `<input>` (or `<textarea>` if `multiline`)
 * - Optional `leadingIcon` slot (left, decorative, pointer-events-none)
 * - Optional `trailingAction` slot (right, the CTA button)
 *   - Animates in/out via Framer when `showActionWhen` is satisfied (default:
 *     value is non-empty)
 * - Optional `loading` state (shimmers the input, disables interaction)
 * - Optional `error` state (border + a11y attributes)
 * - Hybrid controlled / uncontrolled (provide `value`+`onChange` or omit)
 *
 * Why this exists separately from <Input>:
 *   <Input> is a single DOM node primitive. ActionInput is a wrapper that
 *   composes Input + a Framer-animated trailing slot. Different concerns.
 */

export const actionInputVariants = cva(
  [
    "relative flex w-full items-center gap-1 transition-all duration-180 ease-smooth-out",
    "border bg-background ring-offset-background",
    // The focus RING OFFSET lives on the variants that actually draw a ring,
    // not here. When the base owned it, every faceless variant had to REMEMBER
    // to zero it, and three of four forgot: Tailwind emits ring-offset as its
    // own box-shadow, so a transparent field with ring-0 still painted a 2px
    // --background box with no ring inside it. Forgetting to ADD an offset is a
    // flush ring, which is cosmetic; forgetting to ZERO one is a visible box on
    // a field that is meant to be invisible. Putting it on the variants makes
    // the failure mode the harmless one.
    "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring",
  ].join(" "),
  {
    variants: {
      variant: {
        default: "rounded-lg border-input focus-within:ring-offset-2",
        chat: "rounded-2xl border-transparent bg-muted/40 shadow-sm focus-within:bg-background focus-within:border-input focus-within:shadow-glow focus-within:ring-offset-2",
        search: "rounded-full border-input bg-muted/40 focus-within:ring-offset-2",
        ghost: "rounded-none border-transparent bg-transparent shadow-none focus-within:ring-0 focus-within:ring-offset-0",
      },
      size: {
        sm: "min-h-9 px-2 text-xs",
        default: "min-h-10 px-2.5 text-sm",
        lg: "min-h-12 px-3 text-base",
      },
      hasError: {
        true: "border-destructive focus-within:ring-destructive",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      hasError: false,
    },
  },
);

type SharedFieldProps = {
  /** Decorative icon before the input (eg. <Search/>). Click events do not bubble. */
  leadingIcon?: React.ReactNode;
  /**
   * Interactive leading slot — buttons, mics, paperclips. Unlike `leadingIcon`
   * this slot is NOT `pointer-events-none` and is NOT `aria-hidden`, so the
   * children participate in the focus order and receive clicks. Renders to
   * the LEFT of `leadingIcon` when both are provided.
   */
  leadingActions?: React.ReactNode;
  /** The trailing CTA — typically a Button. Animates in/out unless `showActionWhen` is "always". */
  trailingAction?: React.ReactNode;
  /** When the trailing action should be visible. Default: "non-empty". */
  showActionWhen?: "always" | "non-empty" | "focus" | "never";
  /** Show loading shimmer + disable input. */
  loading?: boolean;
  /** Mark as error state (red border + aria-invalid). */
  error?: boolean;
  /** Accessible name for the field if no `<label>` is wired externally. */
  "aria-label"?: string;
  /** Override the wrapper className (not the input itself). */
  wrapperClassName?: string;
};

type ActionInputBaseProps = SharedFieldProps & VariantProps<typeof actionInputVariants>;

type SingleLineProps = ActionInputBaseProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> & {
    multiline?: false;
  };

type MultiLineProps = ActionInputBaseProps &
  Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size"> & {
    multiline: true;
    /** Max rows before vertical scroll appears. Only meaningful when `multiline`. */
    maxRows?: number;
  };

export type ActionInputProps = SingleLineProps | MultiLineProps;

const useAutoResize = (
  ref: React.RefObject<HTMLTextAreaElement>,
  value: string,
  maxRows: number,
) => {
  React.useEffect(() => {
    const t = ref.current;
    if (!t) return;
    t.style.height = "auto";
    if (typeof window === "undefined") return;
    const styles = window.getComputedStyle(t);
    const lineHeight = parseFloat(styles.lineHeight) || 20;
    const padY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom);
    const max = lineHeight * maxRows + padY;
    t.style.height = `${Math.min(t.scrollHeight, max)}px`;
    t.style.overflowY = t.scrollHeight > max ? "auto" : "hidden";
  }, [ref, value, maxRows]);
};

const shouldShowAction = (
  mode: SharedFieldProps["showActionWhen"],
  value: string,
  focused: boolean,
) => {
  switch (mode) {
    case "always":
      return true;
    case "focus":
      return focused;
    case "never":
      return false;
    case "non-empty":
    default:
      return value.length > 0;
  }
};

export const ActionInput = React.forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  ActionInputProps
>((props, ref) => {
  // `multiline` and `maxRows` are part of the discriminated union — they
  // must be destructured here so they don't leak into `...rest` and end
  // up spread onto the underlying `<textarea>` / `<input>` as DOM
  // attributes (React warns: "Received `true` for a non-boolean
  // attribute `multiline`" / "React does not recognize the `maxRows`
  // prop on a DOM element"). Cast first because TS narrows the union by
  // discriminator on the parent, but here we want both fields visible
  // for stripping.
  const {
    className,
    wrapperClassName,
    leadingIcon,
    leadingActions,
    trailingAction,
    showActionWhen = "non-empty",
    loading = false,
    error = false,
    variant,
    size,
    placeholder,
    "aria-label": ariaLabel,
    multiline: _multiline,
    maxRows: _maxRows,
    ...rest
  } = props as ActionInputProps & { multiline?: boolean; maxRows?: number };

  const isMultiline = (props as MultiLineProps).multiline === true;
  const value = (props.value as string | undefined) ?? "";
  const [focused, setFocused] = React.useState(false);

  const fieldRef = React.useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  React.useImperativeHandle(ref, () => fieldRef.current as HTMLInputElement | HTMLTextAreaElement);

  useAutoResize(
    fieldRef as React.RefObject<HTMLTextAreaElement>,
    isMultiline ? value : "",
    isMultiline ? ((props as MultiLineProps).maxRows ?? 8) : 1,
  );

  const showAction =
    trailingAction != null && shouldShowAction(showActionWhen, value, focused) && !loading;

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFocused(true);
    type FocusHandler = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    (rest as { onFocus?: FocusHandler }).onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    window.setTimeout(() => setFocused(false), 100);
    type BlurHandler = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    (rest as { onBlur?: BlurHandler }).onBlur?.(e);
  };

  // Vertical padding scales with `size` so a single-line textarea / input
  // fully occupies the wrapper's `min-h-*` instead of leaving a sliver of
  // empty space that `items-start` / `items-end` / `items-center` only
  // shuffle around. With `lg` (`min-h-12`, text-base) the previous flat
  // `py-2` left ~8px unfilled — visible as a too-tall pill in compact
  // chat-input usage. The scale below makes content height = wrapper
  // min-height for the single-line case across every size, so vertical
  // alignment becomes a no-op until the textarea actually grows.
  const fieldClasses = cn(
    "flex-1 bg-transparent outline-none border-0",
    "placeholder:text-muted-foreground",
    "disabled:cursor-not-allowed disabled:opacity-60",
    size === "sm" ? "py-1.5" : size === "lg" ? "py-3" : "py-2",
    isMultiline && "resize-none",
    loading && "animate-pulse",
    className,
  );

  const sharedFieldProps: Record<string, unknown> = {
    "aria-label": ariaLabel,
    "aria-invalid": error || undefined,
    placeholder,
    disabled: loading || (rest as { disabled?: boolean }).disabled,
    onFocus: handleFocus,
    onBlur: handleBlur,
    className: fieldClasses,
  };

  return (
    <div
      className={cn(
        actionInputVariants({ variant, size, hasError: error }),
        loading && "pointer-events-none",
        wrapperClassName,
      )}
    >
      {leadingActions ? (
        <span className="flex-shrink-0 flex items-center pl-1">{leadingActions}</span>
      ) : null}

      {leadingIcon ? (
        <span
          className="flex-shrink-0 flex items-center text-muted-foreground pointer-events-none pl-1.5"
          aria-hidden
        >
          {leadingIcon}
        </span>
      ) : null}

      {isMultiline ? (
        <textarea
          ref={fieldRef as React.RefObject<HTMLTextAreaElement>}
          rows={1}
          {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)}
          {...sharedFieldProps}
        />
      ) : (
        <input
          ref={fieldRef as React.RefObject<HTMLInputElement>}
          {...(rest as React.InputHTMLAttributes<HTMLInputElement>)}
          {...sharedFieldProps}
        />
      )}

      <AnimatePresence mode="popLayout" initial={false}>
        {showAction && (
          <motion.div
            key="trailing-action"
            layout
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.6, opacity: 0 }}
            transition={{ type: "spring", stiffness: 600, damping: 28 }}
            className="flex-shrink-0 flex items-center pr-0.5"
          >
            {trailingAction}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

ActionInput.displayName = "ActionInput";
